/**
 * A person in the real-time game (WORKBOARD S3 / E2, owner Oct 9: the design lane swaps it in): the
 * E2 soldier (rendering/personModel.ts) posed from a unit's state each frame, replacing the classic
 * unit's model at human scale. The classic model stays the giant class (ruling 24).
 *
 * State → pose: dead hidden; airborne, then landing; operating a drone while tasking or launching it
 * (I8); seated while mounted (S6); running when it moves (the weapon at low ready unless it fired in
 * the last second); otherwise standing with the weapon up at its aim pitch; crouched (a kneel, a low
 * walk) or prone (lying, a crawl) as `stance` says (decision 65). Shots kick (`fired`). The
 * far silhouette tier swaps in by on-screen size, and below a few pixels the body is hidden (the map
 * and the markers carry it). Presentation only: reads state, writes none.
 */
import { weaponKin } from '../core/weapons';
import * as THREE from 'three';
import type { PlayerState, Vec3, UnitRole } from '../types';
import type { UnitPalette } from './unitPalette';
import { skipWhenHidden } from './hiddenWalk';
import type { HelmetKind } from './armourLooks';
import { buildPersonModel, bakePersonModel, createPersonAnim, updatePersonAnimation, setPersonTier, setPersonHand, personTierFor, gaitAt, swapPersonGun, poseGunParts, bakeGl40Kit, PERSON_UNITS, LAW_THROW, CRAWL_LOOK, THROW_LOOK, type PersonModel, type PersonAnim, type PersonState } from './personModel';
import type { Hand } from './handedness';
import { disposeObjectTree, bakeStaticParts } from './meshBake';
import { setAntiDropshipMissileLock, type AntiDropshipMissileModel } from './antiDropshipMissileLooks';
import { buildPlayGun, buildPlayGl40, buildPlaySpentLaw, isPartedGun, keepGunParts, kinOf, type HandGunKind, type TubeKind } from './gunLooks';
import type { SilhouetteCrowd } from './silhouetteCrowd';
import { EJECT, ejectFrom, type RoundKind } from './brass';
import { WEAPON_LOOK, LAW, type LongGun, type WeaponModel, type Gl40Model } from './groundWeapons';
import { BANDAGE, BEACON, GROUND_WEAPONS, GAIT, GRENADES, PHYS_DT } from '../constants';
import { reserveLeft, roundsIn } from '../core/weapons';
import { carriesGl40 } from '../core/loadout';

/** 65.36: the slots whose gun the player's figure swaps for a live twin (the long gun in the rifle's, the pistol, the 40 mm; the tube's one moving part, its trigger bar, is apart on every figure) */
type GunSlot = 'rifle' | 'pistol' | 'gl40';
const GUN_SLOTS: readonly GunSlot[] = ['rifle', 'pistol', 'gl40'];

/** Seconds after a shot that a runner keeps the weapon up (lane C's GAIT.readySeconds; 66.10: in cover too, COVER_FIRE). */
const READY_AFTER_SHOT = GAIT.readySeconds;
/** The slope under the feet eases over this long (owner, Oct 10: a noisy vertical oscillation walking the fine ground). */
export const SLOPE_SMOOTH_S = 0.15;
/** Seconds over which a drawn bullet blends from the drawn muzzle onto the sim's path (which starts at the eye). */
export const MUZZLE_BLEND_S = 0.25;
/** Where to draw a bullet: the sim's position plus the muzzle offset recorded at the shot, fading out over the blend. Pure. */
export function muzzleBlend(pos: Vec3, off: Vec3 | undefined, age: number): Vec3 {
  const f = off ? Math.max(0, 1 - age / MUZZLE_BLEND_S) : 0;
  return f > 0 ? { x: pos.x + off!.x * f, y: pos.y + off!.y * f, z: pos.z + off!.z * f } : pos;
}

const _x = new THREE.Vector3(), _y = new THREE.Vector3(), _z = new THREE.Vector3(), _m = new THREE.Matrix4();
const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _c = new THREE.Vector3(), _s = new THREE.Vector3();

/** X31: a rider's anchors on the bike (rendering/vehicleModels.ts): the saddle's seat, the bars' grips. */
export interface PersonRide { seat: THREE.Object3D; right: THREE.Object3D; left: THREE.Object3D; /** 65.22: 'ground' sits on the ground behind a gun (the emplaced HMG); 'chair' in a gun pit's seat (63.3's follow-up), a cockpit's; 67.63: 'drive' low in a cab, 'bench' a rider's, 'hatch' standing in a hatch; else astride */ pose?: 'saddle' | 'ground' | 'chair' | 'bench' | 'hatch' | 'drive' | 'pilot' }

/**
 * 66.7: the cover pose drawn for a unit (PersonAnim.cover) from the cover it holds in the Match (PlayerState.cover — set by
 * the bots at their posts and by the player's take-cover keys, one field for both): lane E's pose, the side leaned round
 * and whether it is out of the cover. Seated or in the air, none. `was` is kept while the hold is unchanged, so a still
 * unit in cover allocates nothing a frame. `face`: the cover's face at the unit (the host's; lane E's pressed poses). Pure.
 */
export function coverAnim(p: Pick<PlayerState, 'cover' | 'mount' | 'airborne'>, was: PersonAnim['cover'], face: CoverFace | null = null): PersonAnim['cover'] {
  const c = p.cover;
  if (!c || p.mount || p.airborne) return null;
  if (was && was.pose === c.pose && was.side === c.side && was.out === c.out && was.gap === face?.gap && was.height === face?.height) return was;
  return { pose: c.pose, out: c.out, ...(c.side !== undefined ? { side: c.side } : {}), ...(face ? { gap: face.gap, height: face.height } : {}) };
}

/**
 * 67.60, the drawn ground under a person (the owner: "limiting vertical high frequency oscillation in the model when walking on
 * messy terrain, but also keeping feet plausibly on terrain"): the body's drawn height follows the ground averaged over a
 * person round it (the host's slope probes' mean, `slope.lift`: the fine ground's facets every half person shook it at a
 * walk's step rate; a plane's mean is its centre, so a steady slope lags nothing), eased over `seconds` and held within
 * `most` person heights of the ground at its centre; each planted foot stands on the ground under it (PersonAnim.ground),
 * so the legs take up the difference. Drawn only — the sim's position is untouched.
 */
export const GROUND_LOOK = { seconds: 0.08, most: 0.3 };
/** 67.60: the drawn ground as the view samples it (the host's): the planet's centre, and the drawn ground's radius along a direction. */
export interface GroundProbe { centre: Vec3; radiusAt: (dir: Vec3) => number }

/**
 * 66.7 (a)'s rework: the cover's face as the pose reads it (person units): the spot to its drawn face, and its drawn top;
 * `toward` (world, a unit tangent at the spot) the way to the cover — the pose faces it whatever the look (PersonAnim
 * coverBearing). Absent (a dig's face): the pose faces the look, as before.
 */
export interface CoverFace { gap: number; height: number; toward?: Vec3 }

/** Which pose a unit's state asks for (pure; the moving test is the host's measured ground speed). */
export function personStateOf(p: Pick<PlayerState, 'airborne' | 'droneTasking' | 'droneLaunchLeft' | 'mount'>, moving: boolean, wasAirborne: boolean): PersonState | 'land' {
  if (p.airborne) return 'airborne';
  if (wasAirborne) return 'land';
  if (p.droneTasking || (p.droneLaunchLeft ?? 0) > 0) return 'operate';
  if (p.mount) return 'seated';
  return moving ? 'run' : 'idle';
}

export class PersonView {
  readonly holder = new THREE.Group();
  readonly model: PersonModel;
  private readonly anim: PersonAnim = createPersonAnim();
  /** 66.7 (a)'s rework: the face of the cover this person holds (the host sets it before update; null: none or not in cover). */
  coverFace: CoverFace | null = null;
  /** S7: whether the rocket tube is on (GameConfig.rockets; the host sets it): who carries a LAW and its spare */
  rockets = false;
  /** 66.8: whether entrench is on (GameConfig.entrench; the host sets it): who carries the entrenching tool, folded on the pack */
  tool = false;
  /**
   * 66.8: the match's action clock (seconds; the host sets it each frame). The dig's strokes run on it from when this person's
   * `digAt` first appeared — the dig sounds' own count (game/actionSound.ts digSounds) — so the blade bites as the bite is
   * heard, and both hold still while the match does.
   */
  matchSeconds = 0;
  private digSince: number | null = null;
  /** S7's dials (the host sets them; game/lawSpent.ts): seconds a spent tube lies, and how many this person leaves at once */
  spentSeconds: number = LAW_SPENT.seconds;
  spentMost: number = LAW_SPENT.most;
  /** S7: the spent tubes this person has dropped, and the view's own clock for their time on the ground */
  private readonly spent: Array<{ piece: THREE.Group; until: number }> = [];
  private lawDropped = false;
  private clock = 0;
  private last: THREE.Vector3 | null = null;
  private wasAirborne = false;
  /** 67.60: where the body left the ground (its feet, the frame before), for the jump's look. */
  private readonly takeoff = new THREE.Vector3();
  /** 67.60: the last throw seen (the sim's `throwReady`) and the grenades carried then — a new one is a throw, of the kind spent. */
  private throwReady: number | undefined = undefined;
  private smokesHeld = 0;
  /** the ground's slope under the feet, smoothed in time (the probes jump at the fine ground's facet edges) */
  private slope: { x: number; z: number } | null = null;
  /** 67.60: the drawn ground (the host sets it; null: the slope's plane, the body at its feet as given) */
  ground: GroundProbe | null = null;
  /** 67.60: the drawn body over the ground at its centre (world units along up; GROUND_LOOK) */
  private lift = 0;
  /** 67.60: this frame's base for the feet's sampler — the feet given, the ground's radius there, world units a person unit */
  private readonly base = new THREE.Vector3();
  private baseRadius = 0;
  private unit = 1;
  /** 67.60: the drawn ground's height over the drawn base at (x, z) in the holder's frame, person units (PersonAnim.ground) */
  private readonly groundUnder = (x: number, z: number): number => {
    const g = this.ground!, w = this.unit;
    _s.copy(this.base).addScaledVector(_x, x * w).addScaledVector(_z, z * w).sub(_c.set(g.centre.x, g.centre.y, g.centre.z));
    const r = _s.length();
    return (g.radiusAt({ x: _s.x / r, y: _s.y / r, z: _s.z / r }) - this.baseRadius - this.lift) / w;
  };
  /** 67.17: at the far tier, draw through this crowd's instanced batch instead of the own model (null = the own model). */
  crowd: SilhouetteCrowd | null = null;
  /** which crowd batches this person's palette shares */
  readonly paletteKey: string;
  /** 65.12: the long gun's kin (its fit, its pose; the death's model reads it) */
  readonly longGun: LongGun;
  /** 65.43: the tube in the LAW's place (the host sets it: the Panzerfaust for whoever carries one); read when the tube is first built */
  tube: TubeKind = 'rocket';
  /** 65.36: whether this is the player's own figure (setOwn) */
  private own = false;
  /** 65.36: per slot, the gun out of the figure — the live twin (its parts apart) while the baked gun is in, or the baked one while the twin is — and which is in */
  private readonly spareGun: Partial<Record<GunSlot, WeaponModel | null>> = {};
  private readonly liveIn: Record<GunSlot, boolean> = { rifle: false, pistol: false, gl40: false };

  /** `leader` (68.8's look, wired by 68.3b): the squad leader's kit — a role, so the host rebuilds the view when it moves. */
  constructor(private readonly scene: THREE.Scene, readonly palette: UnitPalette, private readonly personHeight: number, /** 65.13 / 65.14: the long gun (65.43: a classic itself, drawn in its kin's place) */ readonly primary: HandGunKind = 'rifle', readonly leader = false, /** 185 / 67.56: the vest drawn (the plate carrier) */ readonly armor: 'none' | 'light' | 'heavy' = 'none', /** 67.56: the helmet drawn (armourLooks.ts drawnHelmet; light = every figure in a match without armour) */ readonly helmet: HelmetKind = 'light', /** 65.43: the sidearm (the M1911 in the pistol's place) */ readonly sidearm: 'pistol' | 'm1911' = 'pistol', /** 201 (8): the medic's or the engineer's kit (roleLooks.ts) */ readonly role: UnitRole | null = null) {
    this.paletteKey = `${palette.body}|${palette.dark}|${palette.flash}|${palette.metal}`;
    this.longGun = kinOf(primary) as LongGun;
    this.model = buildPersonModel(palette, personHeight, { primary, ...(sidearm !== 'pistol' ? { sidearm } : {}), ...(leader ? { leader: true } : {}), ...(armor !== 'none' ? { armor } : {}), ...(helmet !== 'light' ? { helmet } : {}), ...(role ? { role } : {}) });   // 65.13 / 65.14: the kit's long gun in the rifle's place
    bakePersonModel(this.model);
    this.model.root.traverse(skipWhenHidden);   // 62.64 (b): the draw's matrix walk leaves what is hidden (the tier not shown, the poses not held)
    this.holder.add(this.model.root);
    scene.add(this.holder);
  }

  /** 67.21: the strong hand — the model mirrored, or not (cheap; the host sets it every frame). */
  setHand(hand: Hand): void { if (this.model.hand !== hand) setPersonHand(this.model, hand); }

  /**
   * 65.36: the player's own figure (the host sets it each frame: the unit the seat holds). The gun in its hand moves — the
   * slide or bolt on each shot, the reload's cycle — as a twin whose moving parts are kept apart (one draw a part, this
   * figure only: the rifle and the pistol 3, the SMG 2; the gun on the sling or in the holster stays baked). Every other
   * figure's gun is baked, its parts still (no new draw). A twin is built the first time it is wanted, then swapped in and out.
   */
  setOwn(own: boolean): void { this.own = own; }

  /** 65.36: the live twin in `slot` (want) or the baked gun (not). */
  private liveGun(slot: GunSlot, want: boolean): void {
    if (this.liveIn[slot] === want) return;
    if (slot === 'gl40' && !this.model.weapons.gl40) return;   // (the 40 mm's kit is built the first time it is carried)
    if (this.spareGun[slot] === undefined) {   // the first time: the twin, each static part baked as the figure's gun is, the moving parts apart
      const gun = slot === 'gl40' ? buildPlayGl40(this.palette) : buildPlayGun(slot === 'rifle' ? this.primary : this.sidearm, this.palette);
      if (isPartedGun(gun)) {
        keepGunParts(gun);
        if (slot === 'gl40') bakeGl40Kit(gun as typeof gun & Gl40Model); else bakeStaticParts(gun.group, { exclude: [gun.magazine] });
        gun.group.traverse(skipWhenHidden);
      }
      this.spareGun[slot] = isPartedGun(gun) ? gun : null;   // (a gun not in parts: nothing to swap)
      if (!isPartedGun(gun)) disposeObjectTree(gun.group);
    }
    const spare = this.spareGun[slot];
    if (!spare) return;
    this.spareGun[slot] = swapPersonGun(this.model, slot, spare);
    this.liveIn[slot] = want;
  }

  /** A shot from this unit: the weapon kicks, and a runner brings it up. */
  fired(): void { this.anim.firedAt = this.anim.time; }
  /** 65.28: a round came near this person (the Match's `nearMiss` event): the beat (personModel SUPPRESS_LOOK). */
  nearMiss(): void { this.anim.suppressedAt = this.anim.time; }

  /** The drawn muzzle of the weapon in hand (world): the bullet and the flash come out of the gun. */
  /**
   * 65.47: where the gun in hand throws its case now — its port, the way out, its axis (world) — and the round and its delay
   * (the springfield's bolt); null for a gun that throws none (the tube, the 40 mm) or a figure not drawn in full.
   */
  ejection(port: THREE.Vector3, out: THREE.Vector3, axis: THREE.Vector3): { round: RoundKind; delay?: number } | null {
    if (this.model.tier !== 'full') return null;
    const w = this.anim.weapon ?? 'rifle', slot = w === 'pistol' ? 'pistol' : w === 'rifle' ? 'rifle' : null;
    if (!slot) return null;
    const kind = this.model.gunKinds[slot], e = EJECT[kind];
    return e && ejectFrom(this.model.weapons[slot].group, kind, port, out, axis) ? e : null;
  }

  muzzleWorld(out: THREE.Vector3): THREE.Vector3 { return (this.model.weapons[this.anim.weapon ?? 'rifle'] ?? this.model.weapons.rifle).muzzle.getWorldPosition(out); }

  /**
   * Pose and place the person: `pos` its feet, `up` the ground normal there, `heading` its facing in
   * the ground plane; `camera` + `viewportHeight` choose the tier by on-screen size; `slope` the
   * ground's rise per unit along its right and its front, so the feet follow the hill.
   */
  update(p: PlayerState, pos: Vec3, up: Vec3, heading: Vec3, dt: number, camera?: THREE.Camera, viewportHeight = 800, slope?: { x: number; z: number; /** 67.60: the ground averaged round the figure over the ground at its centre (world units) */ lift?: number }, treat?: { left: number; total: number }, ride?: PersonRide): void {
    this.holder.visible = p.alive;
    if (!p.alive) { this.last = null; this.wasAirborne = false; this.slope = null; return; }
    const at = _a.set(pos.x, pos.y, pos.z);
    const speed = this.last && dt > 0 ? at.distanceTo(this.last) / dt : 0;
    if (p.airborne && !this.wasAirborne) this.takeoff.copy(this.last ?? at);
    this.last = (this.last ?? new THREE.Vector3()).copy(at);
    const moving = !p.airborne && speed > 0.25 * this.personHeight;   // a quarter of a body length a second
    const want = personStateOf(p, moving, this.wasAirborne);
    this.wasAirborne = !!p.airborne;
    const a = this.anim;
    if (want === 'land') { a.state = 'landing'; a.landedAt = a.time; a.landSpeed = Math.max(0, -(a.airUp ?? 0)); }
    else if (!(a.state === 'landing' && want === 'idle')) a.state = want;          // a landing plays out unless something else starts
    // 67.60, the jump's look: its speed up the ground's normal and its rise over where it took off (person units), and whether
    // it is a jump with its own feel (63.15: the sim keeps its take-off height)
    if (p.airborne) {
      const n = 1 / (Math.hypot(up.x, up.y, up.z) || 1), toUnits = PERSON_UNITS / this.personHeight, v = p.airborneVel;
      a.airUp = v ? (v.x * up.x + v.y * up.y + v.z * up.z) * n * toUnits : 0;
      a.airRise = ((at.x - this.takeoff.x) * up.x + (at.y - this.takeoff.y) * up.y + (at.z - this.takeoff.z) * up.z) * n * toUnits;
      a.jumpFeel = p.airborneCause === 'jump' && p.jumpFrom !== undefined;
    } else { a.airUp = a.airRise = undefined; a.jumpFeel = undefined; }
    a.speed = (speed / this.personHeight) * PERSON_UNITS;                            // person units a second
    a.aimPitch = p.aimPitch ?? 0;
    a.stance = p.stance;                                                            // decision 65: crouched or prone
    a.gait = p.gait;                                                                // 63.8: a move (the gun low) or a sprint (port arms), eased by the pose
    // 66.8: digging in (PlayerState.digAt) — kneeling with the tool, its stroke from when this dig began here (a step, a seat,
    // the air or a hit ends it in the sim); the tool rides the pack otherwise, with entrench on
    if (p.digAt && !p.mount && !p.airborne) {
      if (this.digSince === null) this.digSince = this.matchSeconds;
      a.digAt = a.time - (this.matchSeconds - this.digSince);   // (the stroke's phase on the match's clock, as the sound's)
    } else { a.digAt = undefined; this.digSince = null; }
    a.tool = this.tool;
    a.cover = p.digAt ? null : coverAnim(p, a.cover, this.coverFace);              // 66.7: held cover, the bots' and the player's alike (66.8: digging, the dig shows)
    a.suppressed = p.suppressed ?? 0;                                              // 65.28: the mark's seconds left (the pose in cover held lower)
    // the ground under the feet, eased over SLOPE_SMOOTH_S so a facet edge is a lean, not a jolt
    if (p.airborne || !slope) this.slope = null;
    else if (!this.slope) this.slope = { x: slope.x, z: slope.z };
    else { const k = Math.min(1, dt / SLOPE_SMOOTH_S); this.slope.x += (slope.x - this.slope.x) * k; this.slope.z += (slope.z - this.slope.z) * k; }
    a.slope = this.slope ?? undefined;
    // 65.5: the weapon in hand, a reload under way (the state counts down; the pose wants when it began), a switch under way
    // S7: the rocket tube is a LAW (the owner: "position and handling looks alright"): in hand, and on the pack (the loaded one and the spare)
    const prevWeapon = a.weapon ?? 'rifle', kin = p.weapon ? weaponKin(p.weapon) : undefined, weapon = kin === 'pistol' ? 'pistol' : kin === 'rocket' ? 'rocket' : kin === 'gl40' ? 'gl40' : 'rifle';   // (65.43: a classic gun as its kin)
    a.gl40 = carriesGl40(p) || undefined;   // 65.31: its own model, in hand or slung across the back
    if (this.rockets) { const spec = GROUND_WEAPONS.rocket; a.lawLoaded = roundsIn(p, 'rocket', spec) > 0; a.lawSpare = reserveLeft(p, 'rocket', spec) > 0; }
    else { a.lawLoaded = undefined; a.lawSpare = undefined; }
    if (weapon !== prevWeapon) a.drawAt = a.time;
    a.weapon = weapon;
    if (!this.model.weapons.rocket) this.model.gunKinds.rocket = this.tube;   // 65.43: the Panzerfaust in the LAW's place, built as such the first time it is carried
    // 65.49 step 2: the anti-dropship missile's lamp at the eye shows the seeker (off with the weapon down)
    if (this.model.gunKinds.rocket === 'missile' && this.model.weapons.rocket) setAntiDropshipMissileLock(this.model.weapons.rocket as AntiDropshipMissileModel, p.missileLock?.state ?? 'off', a.time);
    for (const slot of GUN_SLOTS) this.liveGun(slot, this.own && weapon === slot);   // 65.36: the player's gun in hand moves
    if (p.reloadLeft !== undefined && p.reloadLeft > 0) { const total = p.reloadTime ?? WEAPON_LOOK[weapon].reload; a.reloadFor = total; a.reloadAt = a.time - (total - p.reloadLeft); }
    else a.reloadAt = undefined;
    // 65.7: a bandage being applied (the state counts down; the pose wants when it began)
    // (a medic poses it from the patient's timer, `treat`; a patient being bandaged by a teammate stands still, hands free)
    if (treat) { a.healFor = treat.total; a.healAt = a.time - (treat.total - treat.left); }
    else if (p.plantLeft !== undefined && p.plantLeft > 0) { a.healFor = BEACON.plantSeconds; a.healAt = a.time - (BEACON.plantSeconds - p.plantLeft); }   // 125a: planting reads as the same work, down over it
    else if (p.healLeft !== undefined && p.healLeft > 0 && p.healBy === undefined) { const total = p.healTime ?? BANDAGE.seconds; a.healFor = total; a.healAt = a.time - (total - p.healLeft); }
    else a.healAt = undefined;
    // 67.60: a throw — the sim lets the grenade go the tick it comes in (its next throw waits GRENADES.throwSeconds), so the
    // throw is drawn from that tick on the match's clock (THROW_LOOK)
    if (p.throwReady !== this.throwReady && p.throwReady !== undefined && THROW_LOOK.on) {
      const since = this.matchSeconds - (p.throwReady * PHYS_DT - GRENADES.throwSeconds);
      if (since >= -1e-6 && since < THROW_LOOK.seconds) {
        a.throwAt = a.time - Math.max(0, since);
        a.throwKind = (p.grenades?.smoke ?? 0) < this.smokesHeld ? 'smoke' : 'he';
      }
    }
    this.throwReady = p.throwReady; this.smokesHeld = p.grenades?.smoke ?? 0;
    a.aiming = a.state !== 'run' || a.time - a.firedAt < READY_AFTER_SHOT;
    // X31: astride a bike — the saddle pose, the hands on its bars (the weapons stowed); 65.22: or sitting behind a gun
    a.seat = ride ? ride.pose ?? 'saddle' : undefined;
    a.hands = ride ? { right: ride.right, left: ride.left } : undefined;
    // the holder's frame: up along the ground normal, the front (+Z) along the heading, +X = up × front (the body's left)
    _y.set(up.x, up.y, up.z).normalize();
    _z.set(heading.x, heading.y, heading.z).addScaledVector(_y, -_y.dot(_z.set(heading.x, heading.y, heading.z))).normalize();
    _x.crossVectors(_y, _z);
    // 67.60: the drawn ground — the body's height low-passed against the ground at its centre (GROUND_LOOK), the feet on it
    const most = GROUND_LOOK.most * this.personHeight, lifted = p.airborne || ride ? 0 : Math.max(-most, Math.min(most, slope?.lift ?? 0));
    this.lift = lifted + (this.lift - lifted) * Math.exp(-dt / GROUND_LOOK.seconds);
    const g = this.ground && !ride && !p.airborne ? this.ground : null;
    if (g) { this.base.copy(at); this.baseRadius = _s.set(pos.x - g.centre.x, pos.y - g.centre.y, pos.z - g.centre.z).length(); this.unit = this.personHeight / PERSON_UNITS; }
    a.ground = g && this.model.tier === 'full' ? this.groundUnder : undefined;
    // 67.60: the way to the cover in that frame — the cover pose faces it, the gun turning back to the look
    const tw = a.cover && !ride ? this.coverFace?.toward : undefined;
    a.coverBearing = tw ? Math.atan2(tw.x * _x.x + tw.y * _x.y + tw.z * _x.z, tw.x * _z.x + tw.y * _z.y + tw.z * _z.z) : undefined;
    updatePersonAnimation(this.model, a, dt);
    if (this.own) poseGunParts(this.model, a);   // 65.36: the player's own gun's moving parts (every other gun is baked still)
    this.spentLaw(a, pos, up, heading, dt);
    if (ride) {
      // seated on the bike's saddle anchor, in the bike's own frame (its lean included)
      ride.seat.updateWorldMatrix(true, false);
      ride.seat.matrixWorld.decompose(this.holder.position, this.holder.quaternion, _s);
      if (camera) this.tierFor(camera, viewportHeight, this.holder.position, _y.set(0, 1, 0).applyQuaternion(this.holder.quaternion));
      return;
    }
    // place: feet at pos (67.60: the drawn height low-passed, GROUND_LOOK), in the holder's frame above
    this.holder.position.copy(at).addScaledVector(_y, this.lift);
    this.holder.quaternion.setFromRotationMatrix(_m.makeBasis(_x, _y, _z));
    // tier by on-screen height
    if (camera) this.tierFor(camera, viewportHeight, at, _y);
  }

  /**
   * S7: the spent LAW — laid on the ground beside the person when it is dropped (the throwaway's drop, or a last tube fired
   * with none to follow), lying there for `spentSeconds` (at most `spentMost` at once; LAW_SPENT's by default, the Lab's dials
   * through the host). Presentation only.
   */
  private spentLaw(a: PersonAnim, pos: Vec3, up: Vec3, heading: Vec3, dt: number): void {
    this.clock += dt;
    for (let i = this.spent.length - 1; i >= 0; i--) if (this.clock >= this.spent[i].until) { const s = this.spent[i]; this.scene.remove(s.piece); disposeObjectTree(s.piece); this.spent.splice(i, 1); }
    const rk = a.reloadAt === undefined ? -1 : (a.time - a.reloadAt) / (a.reloadFor ?? WEAPON_LOOK.rocket.reload);
    const dropping = a.weapon === 'rocket' && this.model.gunKinds.rocket !== 'missile' && ((rk >= LAW_THROW.drop && rk < 1) || (rk < 0 && a.lawLoaded === false));
    if (dropping && !this.lawDropped) {
      const piece = new THREE.Group(), tube = buildPlaySpentLaw(this.palette, this.model.gunKinds.rocket);
      tube.position.y = LAW.radius; tube.rotation.y = 0.7; piece.add(tube); piece.userData.spentLaw = true;
      // on the ground at the right foot, a little ahead: the holder's frame (up the ground's normal, the front along the heading)
      const y = new THREE.Vector3(up.x, up.y, up.z).normalize(), h = new THREE.Vector3(heading.x, heading.y, heading.z);
      const z = h.addScaledVector(y, -y.dot(h)).normalize(), x = new THREE.Vector3().crossVectors(y, z);
      piece.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, y, z));
      piece.scale.setScalar(this.personHeight / PERSON_UNITS);
      piece.position.set(pos.x, pos.y, pos.z).addScaledVector(x, (this.model.hand === 'right' ? -0.45 : 0.45) * this.personHeight / PERSON_UNITS).addScaledVector(z, 0.2 * this.personHeight / PERSON_UNITS);
      this.scene.add(piece);
      this.spent.push({ piece, until: this.clock + this.spentSeconds });
      while (this.spent.length > this.spentMost) { const s = this.spent.shift()!; this.scene.remove(s.piece); disposeObjectTree(s.piece); }
    }
    this.lawDropped = dropping;
  }

  /** The tier by on-screen height, standing at `at` with `up`; a far figure joins its palette's crowd. */
  private tierFor(camera: THREE.Camera, viewportHeight: number, at: THREE.Vector3, up: THREE.Vector3): void {
    const foot = _c.copy(at);
    _b.copy(foot).addScaledVector(up, this.personHeight);
    const pa = _a.copy(foot).project(camera), pb = _b.project(camera);
    const px = pa.z < 1 && pb.z < 1 ? Math.abs(pa.y - pb.y) * viewportHeight / 2 : 0;
    const tier = personTierFor(px);
    if (tier !== this.model.tier) setPersonTier(this.model, tier);
    // 67.17: a far figure joins its palette's instanced batch (one draw for all of them) and hides its own
    const crowded = tier === 'silhouette' && this.crowd !== null;
    this.model.root.visible = tier !== 'marker' && !crowded;
    if (crowded) this.crowd!.add(this.model, this.paletteKey);
  }

  /**
   * 67.25: the gait the footsteps follow — the stride phase (in strides; a step at each half) and the run share (0 a walk
   * … 1 a run) — or null when the body takes no steps (still, airborne, seated, operating a drone). The crawl's phase is
   * its knees' swing (personModel.ts: t × (3 + 1.5 speed) radians).
   */
  get gait(): { phase: number; run: number } | null {
    const a = this.anim;
    if (a.state !== 'run') return null;
    if (a.stance === 'prone') return { phase: CRAWL_LOOK.on ? a.gaitPhase ?? 0 : (a.time * (3 + a.speed * 1.5)) / (Math.PI * 2), run: 0 };   // (67.60: the crawl's own stride)
    return { phase: a.gaitPhase ?? 0, run: a.stance === 'crouch' ? 0 : gaitAt(a.gaitSpeed ?? a.speed).run };
  }

  dispose(): void {
    this.scene.remove(this.holder);
    disposeObjectTree(this.holder);
    for (const gun of Object.values(this.spareGun)) if (gun) disposeObjectTree(gun.group);   // (the guns swapped out of the figure)
    for (const s of this.spent) { this.scene.remove(s.piece); disposeObjectTree(s.piece); }
    this.spent.length = 0;
  }
}

/** S7: how long a spent LAW lies where it was dropped (seconds), and the most one person leaves at once. */
export const LAW_SPENT = { seconds: 30, most: 3 };
